import axios from 'axios';
import i18n from './i18n';
import router from './router';
import store from './store';
import emitter from './eventBus';

const apiClient = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

apiClient.interceptors.response.use(
    (response) => {
        if (response.data?.message) {
            const isErrorKey = response.data.message.startsWith('errors.');
            const translatedMessage = i18n.global.t(response.data.message);

            response.data.translatedMessage = translatedMessage;

            if (isErrorKey) {
                emitter.emit('show-toast', {
                    message: translatedMessage,
                    type: 'danger',
                });
            }
        }
        return response;
    },
    async (error) => {
        const status = error.response?.status;
        const contentType = error.response?.headers['content-type'];
        let errorData = error.response?.data;

        if (errorData instanceof Blob && contentType?.includes('application/json')) {
            try {
                const text = await errorData.text();
                errorData = JSON.parse(text);
            } catch (e) {
                // fallback: keep as blob
            }
        }

        const errorMessageKey = errorData?.error || errorData?.message || 'errors.http_error';

        const translatedMessage = errorMessageKey.startsWith('errors.')
            ? i18n.global.t(errorMessageKey)
            : i18n.global.t(`errors.${errorMessageKey}`);

        const toastErrors = {
            403: 'errors.forbidden',
            404: 'errors.not_found',
            405: 'errors.method_not_allowed',
        };

        if (status === 401) {
            const isLoginAttempt = error.config?.url?.includes('/auth/login');
            const tokenExpired = errorData?.error === 'Token expired';
        
            if (isLoginAttempt) {
                return Promise.reject(error);
            }
        
            localStorage.removeItem('token');
            await store.dispatch('auth/logout');
        
            if (router.currentRoute.value.name !== 'LoginPage') {
                if (!tokenExpired) {
                    setTimeout(() => router.push({ name: 'LoginPage' }), 100);
                } else {
                    router.push({ name: 'LoginPage' });
                }
            }
        
            return new Promise(() => {});
        }        

        if (status === 422) {
            let message = translatedMessage;

            if (Array.isArray(errorData?.details)) {
                message = errorData.details.join('\n');
            } else if (
                typeof errorData?.details === 'object' &&
                errorData.details !== null
            ) {
                const allErrors = Object.values(errorData.details).flat();
                message = allErrors.join('\n');
            }

            emitter.emit('show-toast', {
                message,
                type: 'danger',
            });

            return Promise.reject({
                ...error,
                customMessage: message,
                originalMessage: errorMessageKey,
                validationDetails: errorData.details,
            });
        }

        if (toastErrors[status]) {
            emitter.emit('show-toast', {
                message: i18n.global.t(toastErrors[status]),
                type: 'danger',
            });
        } else {
            emitter.emit('show-toast', {
                message: translatedMessage,
                type: 'danger',
            });
        }

        return Promise.reject({
            ...error,
            customMessage: translatedMessage,
            originalMessage: errorMessageKey,
        });
    }
);

export default apiClient;
